import { forwardRef, memo, useState, useCallback, useEffect, useMemo } from 'react';
import isNull from 'lodash/isNull';
import { useLazyQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
// Skillmore UI Components
import { DEFAULT_MENU_RIGHT } from '@empathco/ui-components/src/helpers/menus';
import useQueryObject from '@empathco/ui-components/src/hooks/useQueryObject';
import CloseIconButton from '@empathco/ui-components/src/elements/CloseIconButton';
import CardTitle from '@empathco/ui-components/src/elements/CardTitle';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import CardFooter from '@empathco/ui-components/src/elements/CardFooter';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
// local imports
import { DEV_PLAN_ADVISOR_QUERY } from '../graphql/DevPlanAdvisor';
import { DEV_PLAN_COURSE_QUERY } from '../graphql/DevPlanCourse';
import { DEV_PLAN_OPPORTUNITY_QUERY } from '../graphql/DevPlanOpportunity';
import {
  CourseAdvisorItemType,
  DevPlanCourseQueryDocument, DevPlanAdvisorQueryDocument,
  DevPlanAdvisorQueryQuery, DevPlanCourseQueryQuery,
  DevPlanOpportunityQuery, DevPlanOpportunityDocument
} from '../graphql/types';
import { CourseAdvisorSearchItem, DevPlanCourse, DevPlanAdvisor, DevPlanOpportunity } from '../graphql/customTypes';
import useCustomerSettings from '../config/customer';
import CourseAndAdvisorSearch from './CourseAndAdvisorSearch';
// SCSS imports
import { content, searchInput, confirmButton } from './AddCourseOrAdvisorPopover.module.scss';

type AddCourseOrAdvisorPopoverProps = {
  readonly devplanId: number;
  readonly title?: string | null;
  readonly skillIds: number[];
  readonly excludeCourses?: number[] | null;
  readonly excludeAdvisors?: number[] | null;
  readonly excludeOpportunities?: number[] | null;
  readonly anchorEl?: Element | null;
  readonly onCourse: (course: DevPlanCourse, addedManually: boolean) => void;
  readonly onAdvisor: (advisor: DevPlanAdvisor, addedManually: boolean) => void;
  readonly onOpportunity: (opportunity: DevPlanOpportunity, addedManually: boolean) => void;
  readonly onCancel: () => void;
  readonly disabled?: boolean | null;
  // for Storybook only
  readonly testLoading?: boolean | null;
};

const AddCourseOrAdvisorPopover = forwardRef<HTMLDivElement, AddCourseOrAdvisorPopoverProps>(({
  devplanId,
  title,
  skillIds,
  excludeCourses,
  excludeAdvisors,
  excludeOpportunities,
  anchorEl,
  onCourse,
  onAdvisor,
  onOpportunity,
  onCancel,
  disabled = false,
  testLoading
}, ref) => {
  const { HAS_COURSES, HAS_MENTORING, HAS_DEV_PLAN_OPPORTUNITIES } = useCustomerSettings();

  const courseOptions = useMemo(() => ({ onCompleted: (data: DevPlanCourseQueryQuery) => {
    if (data.devplanCourse) onCourse(data.devplanCourse as DevPlanCourse, true);
  } }), [onCourse]);
  const advisorOptions = useMemo(() => ({ onCompleted: (data: DevPlanAdvisorQueryQuery) => {
    if (data.devplanAdvisor) onAdvisor(data.devplanAdvisor as DevPlanAdvisor, true);
  } }), [onAdvisor]);
  const opportunityOptions = useMemo(() => ({ onCompleted: (data: DevPlanOpportunityQuery) => {
    if (data.devplanOpportunity) onOpportunity(data.devplanOpportunity as DevPlanOpportunity, true);
  } }), [onOpportunity]);

  // lazy load dev plan course
  const courseQuery = useLazyQuery(DEV_PLAN_COURSE_QUERY as typeof DevPlanCourseQueryDocument, courseOptions);
  const { loading: pendingCourse } = useQueryObject({
    data: undefined as unknown as DevPlanCourse,
    key: 'devplanCourse',
    lazyQuery: courseQuery
  });

  // lazy load dev plan advisor
  const advisorQuery = useLazyQuery(DEV_PLAN_ADVISOR_QUERY as typeof DevPlanAdvisorQueryDocument, advisorOptions);
  const { loading: pendingAdvisor } = useQueryObject({
    data: undefined as unknown as DevPlanAdvisor,
    key: 'devplanAdvisor',
    lazyQuery: advisorQuery
  });

  // lazy load dev plan opportunity
  const opportunityQuery = useLazyQuery(
    DEV_PLAN_OPPORTUNITY_QUERY as typeof DevPlanOpportunityDocument, opportunityOptions
  );
  const { loading: pendingOpportunity } = useQueryObject({
    data: undefined as unknown as DevPlanOpportunity,
    key: 'devplanOpportunity',
    lazyQuery: opportunityQuery
  });

  const [execCourse] = courseQuery;
  const [execAdvisor] = advisorQuery;
  const [execOpportunity] = opportunityQuery;

  const pending = pendingCourse || pendingAdvisor || pendingOpportunity;

  const [item, setItem] = useState<CourseAdvisorSearchItem | null>(null);
  // one failure state for the three lookups, cleared before each so the snackbar re-opens every time
  const [addFailure, setAddFailure] = useState<CourseAdvisorItemType | null>(null);

  const runLookup = useCallback((
    itemType: CourseAdvisorItemType,
    exec: () => Promise<{ error?: unknown }>
  ) => {
    setAddFailure(null);
    (async () => {
      try {
        const { error } = await exec();
        if (error) setAddFailure(itemType);
      } catch (_err) {
        setAddFailure(itemType);
      }
    })();
  }, []);

  const isOpen = Boolean(anchorEl);

  const [visible, setVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setItem(null);
    }
  }, [isOpen]);

  const handleSearch = useCallback((itm: CourseAdvisorSearchItem | null) => setItem(itm), []);

  const handleConfirm = useCallback(() => {
    if (item) {
      const { item_type, item_id } = item;
      if (item_type === CourseAdvisorItemType.course) {
        runLookup(item_type, () => execCourse({ variables: { devplan_id: devplanId, course_id: item_id } }));
      } else if (item_type === CourseAdvisorItemType.advisor) {
        runLookup(item_type, () => execAdvisor({ variables: { devplan_id: devplanId, advisor_id: item_id } }));
      } else if (item_type === CourseAdvisorItemType.opportunity) {
        runLookup(item_type, () => execOpportunity({ variables: { devplan_id: devplanId, opportunity_id: item_id } }));
      }
    }
  }, [item, devplanId, runLookup, execCourse, execAdvisor, execOpportunity]);

  const transitionProps = useMemo(() => ({ onExited: () => {
    setVisible(false);
  } }), []);

  useEffect(() => {
    if (testLoading === true) {
      runLookup(CourseAdvisorItemType.course, () => execCourse({ variables: { devplan_id: 123, course_id: 17630 } }));
    } else if (testLoading === false) {
      runLookup(CourseAdvisorItemType.advisor, () => execAdvisor({ variables: { devplan_id: 123, advisor_id: 1 } }));
    } else if (isNull(testLoading)) {
      runLookup(
        CourseAdvisorItemType.opportunity,
        () => execOpportunity({ variables: { devplan_id: 123, opportunity_id: 1 } })
      );
    }
  }, [testLoading, runLookup, execCourse, execAdvisor, execOpportunity]);

  return (
    <>
      <Popover
          ref={ref}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...DEFAULT_MENU_RIGHT}
          open={isOpen}
          anchorEl={anchorEl}
          onClose={onCancel}
          TransitionProps={transitionProps}
      >
        <CloseIconButton small onClick={onCancel}/>
        <CardTitle
            title={title || `hr.dev_plan.add_title${
              HAS_MENTORING ? '' : '.mentoring_off'}${
              HAS_COURSES ? '' : '.courses_off'}${
              HAS_DEV_PLAN_OPPORTUNITIES ? '' : '.opp_off'}`}
            withDivider
        />
        <CardSection className={content}>
          <Box flexGrow={1} py={1} display="flex" alignItems="center" className={searchInput}>
            {visible ? (
              <CourseAndAdvisorSearch
                  fullWidth
                  skillIds={skillIds}
                  excludeAdvisors={excludeAdvisors || undefined}
                  excludeCourses={excludeCourses || undefined}
                  excludeOpportunities={excludeOpportunities || undefined}
                  onChange={handleSearch}
                  disabled={pending || disabled}
              />
            ) : undefined}
          </Box>
        </CardSection>
        <CardFooter withDivider>
          <Button
              onClick={handleConfirm}
              color="primary"
              variant="contained"
              disableElevation
              disabled={pending || disabled || !item}
              startIcon={pending ? <CircularProgress size={18} color="inherit"/> : undefined}
              className={confirmButton}
          >
            <FormattedMessage id="common.button.add"/>
          </Button>
        </CardFooter>
      </Popover>
      <ActionSnackbar
          severity="error"
          message="hr.dev_plan.add_course_error"
          open={addFailure === CourseAdvisorItemType.course}
      />
      <ActionSnackbar
          severity="error"
          message="hr.dev_plan.add_advisor_error"
          open={addFailure === CourseAdvisorItemType.advisor}
      />
      <ActionSnackbar
          severity="error"
          message="hr.dev_plan.add_opportunity_error"
          open={addFailure === CourseAdvisorItemType.opportunity}
      />
    </>
  );
});

AddCourseOrAdvisorPopover.displayName = 'AddCourseOrAdvisorPopover';

export default memo(AddCourseOrAdvisorPopover);
